/* Reducers follow the "ducks" method of exporting
  the constants, reducer and actions for the domain
  with the reducer being the default export.
*/
import { createSelector } from 'reselect';
import Immutable from 'immutable';
import Cookies from 'js-cookie';
import { reducerKeys } from 'edrolo/schemas';
import { createNormalizedStoreUpdater } from 'edrolo/reducers/utils';

export const actionTypes = {
  LOAD_COURSE_PROGRESS_SIGNAL: 'edrolo/progress/LOAD_COURSE_PROGRESS_SIGNAL',
  LOAD_COURSE_PROGRESS_FETCH: 'edrolo/progress/LOAD_COURSE_PROGRESS_FETCH',
  LOAD_COURSE_PROGRESS_SUCCESS: 'edrolo/progress/LOAD_COURSE_PROGRESS_SUCCESS',
  LOAD_COURSE_PROGRESS_FAILURE: 'edrolo/progress/LOAD_COURSE_PROGRESS_FAILURE',

  LOAD_CLASS_PROGRESS_SIGNAL: 'edrolo/progress/LOAD_CLASS_PROGRESS_SIGNAL',
  LOAD_CLASS_PROGRESS_FETCH: 'edrolo/progress/LOAD_CLASS_PROGRESS_FETCH',
  LOAD_CLASS_PROGRESS_SUCCESS: 'edrolo/progress/LOAD_CLASS_PROGRESS_SUCCESS',
  LOAD_CLASS_PROGRESS_FAILURE: 'edrolo/progress/LOAD_CLASS_PROGRESS_FAILURE',

  LOAD_CLASS_LESSON_PROGRESS_SIGNAL:
    'edrolo/progress/LOAD_CLASS_LESSON_PROGRESS_SIGNAL',
  LOAD_CLASS_LESSON_PROGRESS_FETCH:
    'edrolo/progress/LOAD_CLASS_LESSON_PROGRESS_FETCH',
  LOAD_CLASS_LESSON_PROGRESS_SUCCESS:
    'edrolo/progress/LOAD_CLASS_LESSON_PROGRESS_SUCCESS',
  LOAD_CLASS_LESSON_PROGRESS_FAILURE:
    'edrolo/progress/LOAD_CLASS_LESSON_PROGRESS_FAILURE',

  ADD_CHUNK_BOOKMARK: 'edrolo/progress/ADD_CHUNK_BOOKMARK',
  DISMISS_MISTAKES_TOOLTIP: 'edrolo/progress/DISMISS_MISTAKES_TOOLTIP',
};

/* Reducer */
const defaultState = Immutable.fromJS({
  [reducerKeys.COURSE_PROGRESS]: {},
  [reducerKeys.STUDENT_LESSON_PROGRESS]: {}, // indexed by stache id
  [reducerKeys.STUDENT_LESSON_SITTING]: {},
  [reducerKeys.CLASS_LESSON_PROGRESS]: {},

  [reducerKeys.STUDENT_SITTING_PROGRESS]: {},

  [reducerKeys.CLASS_PROGRESS]: {},
  [reducerKeys.CLASS_LESSON_AVERAGE]: {},

  courseProgressLoading: false,
  classProgressLoading: false,

  selfRatingSubmitting: false,
  showMistakesTooltip: !Cookies.get('hide-mistake-tooltip'),
});

export default (state = defaultState, { type, payload }) => {
  switch (type) {
    case actionTypes.LOAD_COURSE_PROGRESS_FETCH:
      return state.set('courseProgressLoading', true);

    case actionTypes.LOAD_COURSE_PROGRESS_SUCCESS: {
      const normalizedUpdater = createNormalizedStoreUpdater(
        [
          reducerKeys.COURSE_PROGRESS,
          reducerKeys.STUDENT_LESSON_PROGRESS,
          reducerKeys.STUDENT_SITTING_PROGRESS,
        ],
        payload.entities
      );

      return normalizedUpdater(state).set('courseProgressLoading', false);
    }

    case actionTypes.LOAD_COURSE_PROGRESS_FAILURE:
      return state.set('courseProgressLoading', false);

    case actionTypes.DISMISS_MISTAKES_TOOLTIP:
      return state.set('showMistakesTooltip', false);

    case actionTypes.LOAD_CLASS_PROGRESS_FETCH:
      return state.set('classProgressLoading', true);

    case actionTypes.LOAD_CLASS_PROGRESS_SUCCESS: {
      // TODO, introspect the schema and do this stuff automatically
      const normalizedUpdater = createNormalizedStoreUpdater(
        [reducerKeys.CLASS_PROGRESS, reducerKeys.CLASS_LESSON_AVERAGE],
        payload.entities
      );

      return normalizedUpdater(state).set('classProgressLoading', false);
    }

    case actionTypes.LOAD_CLASS_PROGRESS_FAILURE:
      return state.set('classProgressLoading', false);

    case actionTypes.LOAD_CLASS_LESSON_PROGRESS_FETCH:
      return state.set('classProgressLoading', true);

    case actionTypes.LOAD_CLASS_LESSON_PROGRESS_SUCCESS: {
      const normalizedUpdater = createNormalizedStoreUpdater(
        [
          reducerKeys.STUDENT_LESSON_PROGRESS,
          [reducerKeys.CLASS_LESSON_PROGRESS, payload.classId], // class lesson progress gets nested by classId
        ],
        payload.entities
      );

      return normalizedUpdater(state).set('classProgressLoading', false);
    }

    case actionTypes.LOAD_CLASS_LESSON_PROGRESS_FAILURE:
      return state.set('classProgressLoading', false);

    default:
      return state;
  }
};

/* action creators */
const loadCourseProgress = {
  signal: (userId, classId) => ({
    type: actionTypes.LOAD_COURSE_PROGRESS_SIGNAL,
    payload: { userId, classId },
  }),
  request: (userId, classId) => ({
    type: actionTypes.LOAD_COURSE_PROGRESS_FETCH,
    payload: { userId, classId },
  }),
  success: (userId, classId, entities) => ({
    type: actionTypes.LOAD_COURSE_PROGRESS_SUCCESS,
    payload: {
      userId,
      classId,
      entities,
    },
  }),
  failure: (errors) => ({
    type: actionTypes.LOAD_COURSE_PROGRESS_FAILURE,
    payload: { errors },
  }),
};

const loadClassProgress = {
  signal: (classId) => ({
    type: actionTypes.LOAD_CLASS_PROGRESS_SIGNAL,
    payload: { classId },
  }),
  request: (classId) => ({
    type: actionTypes.LOAD_CLASS_PROGRESS_FETCH,
    payload: { classId },
  }),
  success: (entities) => ({
    type: actionTypes.LOAD_CLASS_PROGRESS_SUCCESS,
    payload: {
      entities,
    },
  }),
  failure: (errors) => ({
    type: actionTypes.LOAD_CLASS_PROGRESS_FAILURE,
    payload: { errors },
  }),
};

const loadClassLessonProgress = {
  signal: (classId, lessonId) => ({
    type: actionTypes.LOAD_CLASS_LESSON_PROGRESS_SIGNAL,
    payload: { classId, lessonId },
  }),
  request: (classId, lessonId) => ({
    type: actionTypes.LOAD_CLASS_LESSON_PROGRESS_FETCH,
    payload: { classId, lessonId },
  }),
  success: (classId, lessonId, entities) => ({
    type: actionTypes.LOAD_CLASS_LESSON_PROGRESS_SUCCESS,
    payload: {
      classId,
      lessonId,
      entities,
    },
  }),
  failure: (errors) => ({
    type: actionTypes.LOAD_CLASS_LESSON_PROGRESS_FAILURE,
    payload: { errors },
  }),
};

const dismissMistakesTooltip = () => (dispatch) => {
  Cookies.set('hide-mistake-tooltip', true);
  dispatch({ type: actionTypes.DISMISS_MISTAKES_TOOLTIP });
};

export const actions = {
  loadCourseProgress,
  loadClassProgress,
  loadClassLessonProgress,
  dismissMistakesTooltip,
};

export const selectors = {
  getAllCourseProgress: (state) =>
    state.progress.get(reducerKeys.COURSE_PROGRESS),
  getCourseProgressLoading: (state) =>
    state.progress.get('courseProgressLoading'),

  getAllStudentLessonProgress: (state) =>
    state.progress.get(reducerKeys.STUDENT_LESSON_PROGRESS),
  getAllStudentSittingProgress: (state) =>
    state.progress.get(reducerKeys.STUDENT_SITTING_PROGRESS),

  getAllClassProgress: (state) =>
    state.progress.get(reducerKeys.CLASS_PROGRESS),
  getAllClassLessonProgress: (state) =>
    state.progress.get(reducerKeys.CLASS_LESSON_PROGRESS),
  getAllClassLessonAverage: (state) =>
    state.progress.get(reducerKeys.CLASS_LESSON_AVERAGE),
  getClassProgressLoading: (state) =>
    state.progress.get('classProgressLoading'),
};

// this returns a function to lookup via lesson id
selectors.getStudentLessonProgressByLessonId = createSelector(
  [selectors.getAllStudentLessonProgress],
  (allStudentLessonProgress) => (lessonId) =>
    allStudentLessonProgress.filter(
      (studentLessonProgress) =>
        studentLessonProgress.get('lesson_id') === lessonId
    )
);
